/**
 * What a plant *does for the wildlife around it*, as canonical botanical data.
 *
 * ## Why this is part of the flora record rather than a table beside it
 *
 * "Winterberry feeds birds" is a fact about *Ilex verticillata*, true in every garden it survives in.
 * "Winterberry feeds birds here in November" is a fact about a garden. The first belongs on the
 * record; the second is derived. So this module describes resources and their *relationship to the
 * plant's own phenology* — nectar while it blooms, fruit for ninety days after it ripens — and
 * `localization/Ecology.ts` turns that into dates once a site says when the plant blooms.
 *
 * That split is what stops the catalog acquiring a wildlife calendar per ZIP code. There is one
 * winterberry, and it offers fruit for about three months after fruit set, wherever it is planted.
 *
 * ## Deliberately coarse
 *
 * Wildlife groups are broad — "native bees", not *Andrena*. Documented species-level relationships
 * (which oak feeds which hairstreak) are real data that belongs here eventually, and
 * {@link LarvalHostRelation} leaves the slot open with an optional `species` list. Filling it is a
 * sourcing problem, not a schema problem, and shipping empty buckets labelled with binomials nobody
 * checked would be worse than shipping honest groups.
 *
 * Every field is optional at the record level. A plant with no `ecology` block contributes nothing to
 * a wildlife report and produces no error — the catalog is enriched incrementally, and a report that
 * says "42 of 54 species carry ecology data" is more useful than one that refuses to run.
 */

/* --------------------------------- Vocabulary --------------------------------- */

/** What a plant offers that something eats. */
export type FoodResourceKind =
  | 'nectar'
  | 'pollen'
  | 'berries'
  | 'fleshy-fruit'
  | 'seeds'
  | 'nuts'
  | 'host-foliage'
  | 'browse';

export const FOOD_RESOURCE_KINDS: readonly FoodResourceKind[] = [
  'nectar',
  'pollen',
  'berries',
  'fleshy-fruit',
  'seeds',
  'nuts',
  'host-foliage',
  'browse',
];

const FOOD_LABELS: Record<FoodResourceKind, string> = {
  nectar: 'Nectar',
  pollen: 'Pollen',
  berries: 'Berries',
  'fleshy-fruit': 'Fleshy fruit',
  seeds: 'Seed',
  nuts: 'Nuts and mast',
  'host-foliage': 'Host foliage',
  browse: 'Browse',
};

export function foodResourceLabel(kind: FoodResourceKind): string {
  return FOOD_LABELS[kind];
}

/** What a plant offers that something lives in. */
export type HabitatResourceKind =
  | 'nesting'
  | 'cover'
  | 'winter-cover'
  | 'standing-stems'
  | 'leaf-litter'
  | 'branch-structure'
  | 'shelter'
  | 'overwintering'
  | 'pithy-stems'
  | 'nesting-material';

export const HABITAT_RESOURCE_KINDS: readonly HabitatResourceKind[] = [
  'nesting',
  'cover',
  'winter-cover',
  'standing-stems',
  'leaf-litter',
  'branch-structure',
  'shelter',
  'overwintering',
  'pithy-stems',
  'nesting-material',
];

const HABITAT_LABELS: Record<HabitatResourceKind, string> = {
  nesting: 'Nesting',
  cover: 'Cover',
  'winter-cover': 'Winter cover',
  'standing-stems': 'Standing stems',
  'leaf-litter': 'Leaf litter',
  'branch-structure': 'Branch structure',
  shelter: 'Shelter',
  overwintering: 'Overwintering habitat',
  // Distinct from `standing-stems`, which is about the structure staying up through winter. A pithy
  // or hollow stem is a *nest cavity*: cut it and a small carpenter bee moves in. The two are
  // routinely conflated in garden advice and they call for opposite maintenance.
  'pithy-stems': 'Pithy stems',
  'nesting-material': 'Nesting material',
};

export function habitatResourceLabel(kind: HabitatResourceKind): string {
  return HABITAT_LABELS[kind];
}

/** Who turns up. Broad groups; see the note at the top about species-level data. */
export type WildlifeGroup =
  | 'native-bees'
  | 'butterflies'
  | 'moths'
  | 'hummingbirds'
  | 'other-pollinators'
  | 'songbirds'
  | 'other-birds'
  | 'small-mammals'
  | 'large-mammals'
  | 'beneficial-insects';

export const WILDLIFE_GROUPS: readonly WildlifeGroup[] = [
  'native-bees',
  'butterflies',
  'moths',
  'hummingbirds',
  'other-pollinators',
  'songbirds',
  'other-birds',
  'small-mammals',
  'large-mammals',
  'beneficial-insects',
];

const WILDLIFE_LABELS: Record<WildlifeGroup, string> = {
  'native-bees': 'Native bees',
  butterflies: 'Butterflies',
  moths: 'Moths',
  // Split out of `other-pollinators` because they are the group whose access is decided by flower
  // *shape* rather than by season: a tubular red flower is a hummingbird plant and a bee cannot
  // reach into it. See {@link FloralMorphology}.
  hummingbirds: 'Hummingbirds',
  'other-pollinators': 'Other pollinators',
  songbirds: 'Songbirds',
  'other-birds': 'Other birds',
  'small-mammals': 'Small mammals',
  'large-mammals': 'Large mammals',
  'beneficial-insects': 'Beneficial insects',
};

export function wildlifeGroupLabel(group: WildlifeGroup): string {
  return WILDLIFE_LABELS[group];
}

/* ------------------------------ Floral morphology ----------------------------- */

/**
 * The shape of the flower, which decides *who can use it*.
 *
 * Every other field in this file answers "what does this plant offer, and when". This one answers
 * "and who can actually get at it", and it is not derivable from either: two shrubs can bloom on the
 * same day with the same nectar value and feed entirely different animals because one holds its
 * nectar in an open disc a hoverfly can land on and the other in a 30 mm tube only a long tongue
 * reaches. A pollinator report built on timing alone quietly assumes every flower is open, which is
 * the single most common way garden wildlife advice goes wrong.
 *
 * - `open` — flat or shallow (Cornus, Viburnum, Rosa): accessible to almost everything, including
 *   short-tongued bees, flies and beetles.
 * - `composite` — many tiny florets on one landing platform (Echinacea, Solidago, Aster): the
 *   workhorse form, high yield per visit.
 * - `tubular` — corolla tube (Monarda, Lonicera, Salvia): long-tongued bees, butterflies, moths and
 *   hummingbirds; excludes most short-tongued visitors.
 * - `bell` — nodding or urn-shaped (Vaccinium, Kalmia): buzz-pollinated by bumblebees.
 * - `catkin` — reduced flowers on a hanging spike (Salix, Betula): early, and a critical pollen
 *   source even where they are also wind-pollinated.
 * - `wind-pollinated` — flowers, but offers no nectar (most Quercus, Poaceae). Recorded so a report
 *   can say "this plant is not a nectar plant" rather than leaving a suspicious blank.
 * - `none` — produces no flowers at all: ferns and any other spore-bearing plant. Distinct from
 *   `wind-pollinated`, which does flower. Conflating the two would have a report describe a
 *   Christmas fern's pollen strategy, which is not a thing it has.
 */
export type FloralMorphology =
  | 'open'
  | 'composite'
  | 'tubular'
  | 'bell'
  | 'catkin'
  | 'wind-pollinated'
  | 'none';

export const FLORAL_MORPHOLOGIES: readonly FloralMorphology[] = [
  'open',
  'composite',
  'tubular',
  'bell',
  'catkin',
  'wind-pollinated',
  'none',
];

const MORPHOLOGY_LABELS: Record<FloralMorphology, string> = {
  open: 'Open / flat',
  composite: 'Composite head',
  tubular: 'Tubular',
  bell: 'Bell / urn',
  catkin: 'Catkin',
  'wind-pollinated': 'Wind-pollinated',
  none: 'Non-flowering',
};

export function floralMorphologyLabel(morphology: FloralMorphology): string {
  return MORPHOLOGY_LABELS[morphology];
}

/**
 * A specialist relationship — an animal that cannot substitute this plant for another.
 *
 * Kept apart from {@link FloraFoodResource} for the same reason {@link LarvalHostRelation} is: a
 * generalist bee that visits a hundred plants losing one of them is an inconvenience, and an
 * oligolectic bee that provisions its brood from a single genus losing that genus is a local
 * extinction. Only the second is a *reason to plant something*, and a data model that scores them
 * with the same 0–1 value can never say so.
 */
export interface SpecialistRelation {
  group: WildlifeGroup;
  /** What the relationship is for: pollen for brood, nectar, or larval foliage. */
  resource: 'pollen' | 'nectar' | 'foliage';
  /** Named species where the record actually carries them. Empty is honest; invented is not. */
  species?: string[];
  /** Roughly how many specialists this plant supports, where a figure is documented. */
  speciesCount?: number;
  note?: string;
}

/* ------------------------------ Resource records ------------------------------ */

/**
 * Which of the plant's own events a resource hangs off.
 *
 * This is the hinge of the whole design. A resource does not carry dates; it names the phenological
 * phase it follows, and the localized phenology supplies the dates. Change the site and every
 * resource moves with the plant, in one place, automatically.
 *
 * - `bloom` — while the flower is open (nectar, pollen).
 * - `fruit` — from fruit maturity until the fruit is gone (berries, fleshy fruit).
 * - `seed` — from seed set until the seed head is stripped or flattened.
 * - `foliage` — while the plant is in leaf (host foliage, browse).
 * - `year-round` — independent of phenology: an evergreen's cover, a trunk's bark.
 */
export type EcologyPhase = 'bloom' | 'fruit' | 'seed' | 'foliage' | 'year-round';

export interface FloraFoodResource {
  kind: FoodResourceKind;
  phase: EcologyPhase;
  /** How much this plant offers relative to others offering the same thing, in [0, 1]. */
  value: number;
  /** Who eats it. */
  consumers: WildlifeGroup[];
  /** One line, where the relationship is worth stating in words. */
  note?: string;
}

export interface FloraHabitatResource {
  kind: HabitatResourceKind;
  /** In [0, 1], read the same way as a food resource's value. */
  value: number;
  /** When the structure is actually there. `year-round` for evergreens and woody frameworks. */
  phase: EcologyPhase;
  users: WildlifeGroup[];
  note?: string;
}

/**
 * A larval host relationship — the one ecological fact that is not interchangeable.
 *
 * A garden full of nectar and no host plants raises no butterflies. Kept separate from
 * {@link FloraFoodResource} because "something eats the leaves" and "this is where a species can
 * complete its life cycle" are different claims, and only the second one can be a bottleneck.
 */
export interface LarvalHostRelation {
  group: WildlifeGroup;
  /** Roughly how many species this plant hosts, where a figure is documented. */
  speciesCount?: number;
  /** Named species, when the record actually carries them. Empty is honest; invented is not. */
  species?: string[];
  note?: string;
}

export interface FloraEcologyDefinition {
  food?: FloraFoodResource[];
  habitat?: FloraHabitatResource[];
  larvalHosts?: LarvalHostRelation[];
  /**
   * Flower shape, which decides who can reach the nectar this plant offers. Absent means the record
   * has not been asked yet — never "open", which is the assumption that makes a pollinator report
   * flattering and wrong.
   */
  floralMorphology?: FloralMorphology;
  /** Animals with an obligate relationship to this plant. See {@link SpecialistRelation}. */
  specialists?: SpecialistRelation[];
  /**
   * A keystone genus for its region — the small number of plants that carry a disproportionate share
   * of the local insect food web. Flagged rather than scored, because the claim is qualitative.
   */
  keystone?: boolean;
  /** Reliably passed over by browsing deer. Affects planting advice, not the food web. */
  deerResistant?: boolean;
}

/* ---------------------------------- Helpers ---------------------------------- */

/** Every wildlife group a record mentions, food and habitat alike. */
export function wildlifeGroupsOf(ecology: FloraEcologyDefinition | undefined): WildlifeGroup[] {
  if (!ecology) return [];
  const groups = new Set<WildlifeGroup>();
  for (const resource of ecology.food ?? []) for (const group of resource.consumers) groups.add(group);
  for (const resource of ecology.habitat ?? []) for (const group of resource.users) groups.add(group);
  for (const host of ecology.larvalHosts ?? []) groups.add(host.group);
  for (const specialist of ecology.specialists ?? []) groups.add(specialist.group);
  return WILDLIFE_GROUPS.filter((group) => groups.has(group));
}

/**
 * A single 0–1 figure for "how much does this plant do for wildlife".
 *
 * A blunt instrument, and used only for ranking and for the "most valuable plants on this site" list.
 * Food counts double what habitat does because a food gap starves and a cover gap merely exposes;
 * larval hosting and specialist relationships are worth more than either, because nothing else
 * substitutes for them.
 *
 * Adding specialists does not restate the record's food entries: a plant already scored for its
 * pollen is scored again here only if some animal cannot get that pollen anywhere else, which is a
 * different claim about the same nectar.
 */
export function ecologyScore(ecology: FloraEcologyDefinition | undefined): number {
  if (!ecology) return 0;
  const food = sum(ecology.food ?? [], (resource) => resource.value);
  const habitat = sum(ecology.habitat ?? [], (resource) => resource.value);
  const hosts = (ecology.larvalHosts ?? []).length;
  const specialists = (ecology.specialists ?? []).length;
  const raw =
    food * 2 + habitat + hosts * 1.5 + specialists * 1.5 + (ecology.keystone === true ? 2 : 0);
  // Saturating rather than linear: the difference between a plant that does nothing and one that
  // does something is the interesting part, and the top of the range should not be reachable only by
  // records that happen to have been enriched in more detail.
  return 1 - Math.exp(-raw / 5);
}

function sum<T>(items: readonly T[], of: (item: T) => number): number {
  let total = 0;
  for (const item of items) total += of(item);
  return total;
}
